import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Pagination,
  Spin,
  Empty,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Tooltip,
  DatePicker,
  Input,
  Select,
  Modal,
  Descriptions,
  Divider
} from 'antd';
import {
  ReloadOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  SearchOutlined,
  FilterOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { approvalService } from '../../services';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { Option } = Select;

const CommissionApproval = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} hóa đơn`,
    pageSizeOptions: ['10', '20', '50', '100'],
    defaultPageSize: 20,
  });
  const [statistics, setStatistics] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    statusCount: {},
    nhomdailyCount: {}
  });

  // Filters - cập nhật theo API mới
  const [filters, setFilters] = useState({
    invoiceCodeLike: '',
    statusIdFilter: null,
    dailyStatusFilter: null,
    dateRange: null
  });

  // Commission calculation states
  const [commissionModal, setCommissionModal] = useState({
    visible: false,
    loading: false,
    selectedInvoice: null,
    result: null
  });

  // Debug mode để test UI với mock data
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, [pagination.current, pagination.pageSize]);

  // Effect để tự động tải lại khi filters thay đổi (trừ invoiceCodeLike - chỉ khi search)
  useEffect(() => {
    if (filters.statusIdFilter !== null || filters.dailyStatusFilter !== null) {
      loadInvoices(true);
    }
  }, [filters.statusIdFilter, filters.dailyStatusFilter]);

  const loadInvoices = async (resetPage = false) => {
    try {
      setLoading(true);
      
      // Thông báo đang tải
      if (!resetPage && pagination.current > 1) {
        message.loading(`Đang tải trang ${pagination.current}...`, 0.5);
      }
      
      const offset = resetPage ? 0 : (pagination.current - 1) * pagination.pageSize;
      
      // Chuẩn bị tham số với filter mới
      const params = {
        limit: pagination.pageSize,
        offset: offset
      };

      // Thêm các filter nếu có giá trị
      if (filters.invoiceCodeLike && filters.invoiceCodeLike.trim()) {
        params.invoiceCodeLike = filters.invoiceCodeLike.trim();
      }

      if (filters.statusIdFilter !== null) {
        params.statusIdFilter = filters.statusIdFilter;
      }

      if (filters.dailyStatusFilter !== null) {
        params.dailyStatusFilter = filters.dailyStatusFilter;
      }
      
      const response = await approvalService.getEligibleInvoices(params);

      if (response.success) {
        const { invoices: invoiceList, pagination: paginationData } = response.data;
        
        setInvoices(invoiceList);
        
        // Update pagination - Sử dụng logic để tính total
        const hasMorePages = invoiceList.length === pagination.pageSize;
        const estimatedTotal = hasMorePages 
          ? (pagination.current * pagination.pageSize) + 1 
          : ((pagination.current - 1) * pagination.pageSize) + invoiceList.length;

        setPagination(prev => ({
          ...prev,
          current: resetPage ? 1 : prev.current,
          total: estimatedTotal
        }));

        // Calculate statistics
        calculateStatistics(invoiceList);
        
        message.success(`Tải thành công ${invoiceList.length} hóa đơn`);
      } else {
        message.error(response.message || 'Không thể tải danh sách hóa đơn');
        setInvoices([]);
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi tải danh sách hóa đơn');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (invoiceList) => {
    const stats = {
      totalInvoices: invoiceList.length,
      totalAmount: 0,
      statusCount: {},
      nhomdailyCount: {}
    };

    invoiceList.forEach(invoice => {
      // Total amount
      stats.totalAmount += invoice.total_amount || 0;

      // Status count
      const statusId = invoice.status_id;
      stats.statusCount[statusId] = (stats.statusCount[statusId] || 0) + 1;

      // Nhomdaily count
      const nhomdailyStatus = invoice.nhomdaily_status || 'Không có';
      stats.nhomdailyCount[nhomdailyStatus] = (stats.nhomdailyCount[nhomdailyStatus] || 0) + 1;
    });

    setStatistics(stats);
  };

  const handleTableChange = (paginationConfig) => {
    // Scroll to top khi chuyển trang
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }));
  };

  // Thêm hàm next/prev page riêng cho các nút bổ sung
  const goToPage = (page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setPagination(prev => ({
      ...prev,
      current: page
    }));
  };

  // Mock data để test UI khi server lỗi
  const getMockCommissionResult = (invoice) => {
    return {
      success: true,
      message: "Tính thù lao thành công (Mock data để demo)",
      data: {
        mahoadonxuat: invoice.code,
        eligibilityCheck: {
          is_valid: true,
          message: "Hóa đơn hợp lệ để tính thù lao"
        },
        commissionResults: [
          {
            memberid: 1001,
            cap: 1,
            thu_lao: 150000,
            doanh_so: invoice.total_amount,
            ty_le: 5.0,
            thu_nhap_khach_truc_tiep: 50000,
            loai_khach: "VIP"
          },
          {
            memberid: 1002,
            cap: 2,
            thu_lao: 75000,
            doanh_so: invoice.total_amount,
            ty_le: 2.5,
            thu_nhap_khach_truc_tiep: 0,
            loai_khach: "Đại lý"
          }
        ],
        updateStatus: {
          success: true,
          message: "Cập nhật trạng thái thành công"
        },
        totalCommissionAmount: 225000,
        affectedMembers: 2
      }
    };
  };

  // Xử lý tính thù lao đại lý
  const handleCalculateCommission = async (invoice, useMock = false) => {
    setCommissionModal({
      visible: true,
      loading: true,
      selectedInvoice: invoice,
      result: null
    });

    try {
      let result;
      
      if (useMock || debugMode) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        result = getMockCommissionResult(invoice);
      } else {
        result = await approvalService.calculateAgentCommission(invoice.code);
      }
      
      setCommissionModal(prev => ({
        ...prev,
        loading: false,
        result: result
      }));

      if (result.success) {
        message.success(result.message);
        // Reload dữ liệu để cập nhật trạng thái
        loadInvoices();
      } else {
        message.error(result.message || 'Có lỗi xảy ra khi tính thù lao');
      }
    } catch (error) {

      
      // Xử lý các loại lỗi cụ thể
      let errorMessage = '';
      let resultMessage = '';
      let serverMessage = '';
      
      // Lấy thông báo từ server nếu có
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          serverMessage = error.response.data;
        } else if (error.response.data.message) {
          serverMessage = error.response.data.message;
        } else if (error.response.data.error) {
          serverMessage = error.response.data.error;
        }
      }
      
      if (error.response?.status === 404) {
        errorMessage = 'API tính thù lao chưa được triển khai trên server.';
        resultMessage = 'Chức năng đang được phát triển. Vui lòng liên hệ admin.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập đã hết hạn.';
        resultMessage = 'Vui lòng đăng nhập lại để tiếp tục.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Lỗi server khi tính thù lao.';
        
        // Thêm thông tin chi tiết cho developer
        if (serverMessage) {
          resultMessage = `Chi tiết lỗi: ${serverMessage}`;
        } else {
          resultMessage = 'Server đang gặp sự cố. Vui lòng thử lại sau ít phút.';
        }
        
        // Cung cấp option sử dụng mock data để demo
        resultMessage += '\n\n💡 Tip: Bạn có thể bật Debug Mode để xem demo UI với dữ liệu mẫu.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Không thể kết nối tới server.';
        resultMessage = 'Kiểm tra kết nối mạng và thử lại.';
      } else {
        errorMessage = 'Lỗi không xác định khi tính thù lao.';
        resultMessage = serverMessage || error.message || 'Vui lòng thử lại hoặc liên hệ admin.';
      }
      
      setCommissionModal(prev => ({
        ...prev,
        loading: false,
        result: {
          success: false,
          message: errorMessage,
          error: resultMessage,
          data: {
            reason: resultMessage
          }
        }
      }));
      
      message.error(errorMessage);
    }
  };

  const handleCloseCommissionModal = () => {
    setCommissionModal({
      visible: false,
      loading: false,
      selectedInvoice: null,
      result: null
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getStatusColor = (statusId) => {
    const colorMap = {
      1: 'orange',     // Chưa xử lý
      2: 'green',      // Đã thanh toán
      6: 'blue'        // Hoàn thành
    };
    return colorMap[statusId] || 'default';
  };

  const getStatusText = (statusId) => {
    const textMap = {
      1: 'Chưa xử lý',
      2: 'Đã thanh toán',
      6: 'Hoàn thành'
    };
    return textMap[statusId] || `Trạng thái ${statusId}`;
  };

  const getNhomdailyStatusColor = (status) => {
    const colorMap = {
      'Đã duyệt': 'success',
      'Chưa duyệt': 'warning', 
      'hủy': 'error',
      'Không có': 'default'
    };
    return colorMap[status] || 'default';
  };

  const getNhomdailyStatusText = (status) => {
    // Giờ status đã là text rồi, không cần convert
    return status || 'Không có';
  };

  const columns = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'code',
      key: 'code',
      width: 140,
      render: (text) => (
        <Text strong style={{ color: '#1890ff' }}>{text}</Text>
      ),
      sorter: true
    },
    {
      title: 'Khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 150,
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis style={{ maxWidth: 130, display: 'block' }}>
            <UserOutlined style={{ marginRight: 4, color: '#52c41a' }} />
            {text}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      render: (amount) => (
        <Text strong style={{ color: '#f5222d' }}>
          {formatCurrency(amount)}
        </Text>
      ),
      sorter: true
    },
    {
      title: 'Trạng thái HĐ',
      dataIndex: 'status_id',
      key: 'status_id',
      width: 120,
      render: (statusId) => (
        <Tag color={getStatusColor(statusId)}>
          {getStatusText(statusId)}
        </Tag>
      ),
      filters: [
        { text: 'Chưa xử lý', value: 1 },
        { text: 'Đã thanh toán', value: 2 },
        { text: 'Hoàn thành', value: 6 }
      ]
    },
    {
      title: 'Nhóm đại lý',
      dataIndex: 'nhomdaily_status',
      key: 'nhomdaily_status',
      width: 120,
      render: (status) => (
        <Tag color={getNhomdailyStatusColor(status)}>
          {getNhomdailyStatusText(status)}
        </Tag>
      ),
      filters: [
        { text: 'Đã duyệt', value: 'duyet' },
        { text: 'Chờ duyệt', value: 'cho_duyet' },
        { text: 'Đã hủy', value: 'huy' },
        { text: 'Không có', value: null }
      ]
    },
    {
      title: 'Phương thức TT',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 120,
      render: (method) => (
        <Tag color="blue">{method}</Tag>
      )
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          <CalendarOutlined style={{ marginRight: 4, color: '#1890ff' }} />
          {new Date(date).toLocaleDateString('vi-VN')}
        </Text>
      ),
      sorter: true
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      width: 200,
      render: (note) => (
        <Tooltip title={note}>
          <Text ellipsis style={{ maxWidth: 180, display: 'block' }}>
            {note || '-'}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => {
        const isApproved = record.nhomdaily_status === 'Đã duyệt';
        const isPending = record.nhomdaily_status === 'Chưa duyệt';
        
        return (
          <Space size="small" direction="vertical" style={{ width: '100%' }}>
            {isPending ? (
              <Tooltip title="Phải duyệt đại lý trước khi tính thù lao">
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApproveEmployee(record)}
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', width: '100%' }}
                >
                  Duyệt ĐL
                </Button>
              </Tooltip>
            ) : isApproved ? (
              <Tooltip title={debugMode ? "Tính thù lao đại lý (Debug Mode - Mock Data)" : "Tính thù lao đại lý"}>
                <Button
                  type="primary"
                  size="small"
                  icon={<CalculatorOutlined />}
                  onClick={() => handleCalculateCommission(record, debugMode)}
                  style={{ 
                    backgroundColor: debugMode ? '#722ed1' : '#52c41a', 
                    borderColor: debugMode ? '#722ed1' : '#52c41a',
                    width: '100%' 
                  }}
                >
                  {debugMode ? '🧪 TL' : 'Tính TL'}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Nhóm đại lý đã bị hủy">
                <Button
                  size="small"
                  disabled
                  style={{ width: '100%' }}
                >
                  Không khả dụng
                </Button>
              </Tooltip>
            )}
            
            <Tooltip title="Xem chi tiết">
              <Button
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => handleViewDetail(record)}
                style={{ width: '100%' }}
              />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  const handleViewDetail = (record) => {
    Modal.info({
      title: 'Chi tiết hóa đơn',
      width: 600,
      content: (
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Mã hóa đơn">{record.code}</Descriptions.Item>
          <Descriptions.Item label="Khách hàng">{record.customer_name}</Descriptions.Item>
          <Descriptions.Item label="Tổng tiền">{formatCurrency(record.total_amount)}</Descriptions.Item>
          <Descriptions.Item label="Trạng thái">{getStatusText(record.status_id)}</Descriptions.Item>
          <Descriptions.Item label="Nhóm đại lý">{getNhomdailyStatusText(record.nhomdaily_status)}</Descriptions.Item>
          <Descriptions.Item label="Phương thức TT">{record.payment_method}</Descriptions.Item>
          <Descriptions.Item label="Ngày tạo">
            {new Date(record.created_at).toLocaleString('vi-VN')}
          </Descriptions.Item>
          <Descriptions.Item label="Ghi chú" span={2}>{record.note || 'Không có'}</Descriptions.Item>
        </Descriptions>
      )
    });
  };

  const handleRefresh = () => {
    loadInvoices(true);
  };

  // Xử lý duyệt nhân viên vào nhóm đại lý
  const handleApproveEmployee = async (record) => {
    try {
      // Hiển thị confirm dialog
      Modal.confirm({
        title: 'Xác nhận duyệt đại lý',
        content: (
          <div>
            <p>Bạn có chắc chắn muốn duyệt nhân viên này vào nhóm đại lý?</p>
            <p><strong>Khách hàng:</strong> {record.customer_name}</p>
            <p><strong>Mã hóa đơn:</strong> {record.code}</p>
          </div>
        ),
        okText: 'Duyệt',
        cancelText: 'Hủy',
        okType: 'primary',
        onOk: async () => {
          try {
            // Gọi API duyệt với created_by làm IDUsers
            const response = await approvalService.approveEmployee(record.created_by);
            
            if (response.success) {
              message.success('Duyệt đại lý thành công!');
              // Reload dữ liệu để cập nhật trạng thái
              loadInvoices();
            } else {
              message.error(response.message || 'Có lỗi xảy ra khi duyệt đại lý');
            }
          } catch (error) {

            
            // Xử lý các loại lỗi cụ thể
            let errorMessage = '';
            if (error.response?.status === 404) {
              errorMessage = 'API duyệt nhân viên chưa được triển khai. Vui lòng liên hệ admin.';
            } else if (error.response?.status === 401) {
              errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
            } else if (error.response?.status === 500) {
              errorMessage = 'Lỗi server khi duyệt nhân viên. Vui lòng thử lại sau.';
            } else if (error.message.includes('Network Error')) {
              errorMessage = 'Không thể kết nối tới server. Kiểm tra kết nối mạng.';
            } else {
              errorMessage = 'Lỗi không xác định. Vui lòng thử lại hoặc liên hệ admin.';
            }
            
            message.error(errorMessage);
          }
        }
      });
    } catch (error) {
      message.error('Có lỗi xảy ra');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card>
            <Row align="middle" justify="space-between">
              <Col>
                <Title level={3} style={{ margin: 0 }}>
                  <DollarOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  Tinh thù lao - Hóa đơn đủ điều kiện
                </Title>
                <Text type="secondary">
                  Quản lý danh sách hóa đơn đủ điều kiện tính thù lao
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    type={debugMode ? "danger" : "default"}
                    onClick={() => setDebugMode(!debugMode)}
                    style={{ 
                      backgroundColor: debugMode ? '#ff7875' : undefined,
                      borderColor: debugMode ? '#ff7875' : undefined,
                      color: debugMode ? '#fff' : undefined
                    }}
                  >
                    {debugMode ? '🔧 Debug ON' : '🔧 Debug Mode'}
                  </Button>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    loading={loading}
                  >
                    Làm mới
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* API Status Notice */}
      <Alert
        message={debugMode ? "🧪 Debug Mode: Sử dụng dữ liệu mẫu" : "Thông báo về chức năng tính thù lao"}
        description={
          <div>
            {debugMode ? (
              <>
                <p>🧪 <strong>Debug Mode đang BẬT:</strong> Chức năng tính thù lao sẽ sử dụng dữ liệu mẫu</p>
                <p>• Phù hợp để demo UI và test workflow</p>
                <p>• Không gọi API thật, không ảnh hưởng dữ liệu thực</p>
                <p>• Tắt Debug Mode để sử dụng API thật</p>
              </>
            ) : (
              <>
                <p>⚠️ <strong>API tính thù lao và duyệt đại lý đang được phát triển.</strong></p>
                <p>• <strong>Quy tắc:</strong> Chỉ nhóm đại lý "Đã duyệt" mới được tính thù lao</p>
                <p>• <strong>Nếu "Chưa duyệt":</strong> Sử dụng nút "Duyệt ĐL" trước khi tính thù lao</p>
                <p>• <strong>Lỗi 500:</strong> Server error, bật Debug Mode để xem demo</p>
                <p>• <strong>Lỗi 401:</strong> Token hết hạn, vui lòng đăng nhập lại</p>
              </>
            )}
          </div>
        }
        type={debugMode ? "warning" : "info"}
        showIcon
        style={{ marginBottom: '24px' }}
        closable
      />

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng hóa đơn"
              value={statistics.totalInvoices}
              prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng giá trị"
              value={statistics.totalAmount}
              formatter={(value) => formatCurrency(value)}
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Đã duyệt nhóm"
              value={statistics.nhomdailyCount['Đã duyệt'] || 0}
              prefix={<UserOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Chưa duyệt nhóm"
              value={statistics.nhomdailyCount['Chưa duyệt'] || 0}
              prefix={<UserOutlined style={{ color: '#fa8c16' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Search
            placeholder="Tìm theo mã hóa đơn..."
            value={filters.invoiceCodeLike}
            onChange={(e) => setFilters(prev => ({ ...prev, invoiceCodeLike: e.target.value }))}
            onSearch={() => loadInvoices(true)}
            enterButton={<SearchOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder="Lọc theo trạng thái HĐ"
            value={filters.statusIdFilter}
            onChange={(value) => {
              setFilters(prev => ({ ...prev, statusIdFilter: value }));
              // Tự động load lại khi thay đổi filter
              setTimeout(() => loadInvoices(true), 100);
            }}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value={1}>Chưa xử lý</Option>
            <Option value={2}>Đã thanh toán</Option>
            <Option value={6}>Hoàn thành</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder="Lọc theo nhóm đại lý"
            value={filters.dailyStatusFilter}
            onChange={(value) => {
              setFilters(prev => ({ ...prev, dailyStatusFilter: value }));
              // Tự động load lại khi thay đổi filter
              setTimeout(() => loadInvoices(true), 100);
            }}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="Đã duyệt">Đã duyệt</Option>
            <Option value="Chưa duyệt">Chưa duyệt</Option>
            <Option value="hủy">Hủy</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={() => loadInvoices(true)}
            style={{ width: '100%', marginBottom: 8 }}
          >
            Áp dụng bộ lọc
          </Button>
          <Button
            onClick={() => {
              setFilters({
                invoiceCodeLike: '',
                statusIdFilter: null,
                dailyStatusFilter: null,
                dateRange: null
              });
              setTimeout(() => loadInvoices(true), 100);
            }}
            style={{ width: '100%' }}
          >
            Xóa bộ lọc
          </Button>
        </Col>
      </Row>

      {/* Info Alert */}
      <Alert
        message="Quy trình tính thù lao và duyệt đại lý"
        description={
          <div>
            <p><strong>📋 Điều kiện hóa đơn đủ tiêu chuẩn:</strong></p>
            <ul style={{ marginLeft: 20 }}>
              <li><strong>Loại 1:</strong> Hóa đơn có status_id = 1 (Chưa xử lý) hoặc 2 (Đã thanh toán)</li>
              <li><strong>Loại 2:</strong> Hóa đơn có status_id = 6 (Hoàn thành) + note = 'Đã hoàn thành đơn - Nhận tại quầy' + customer_name ≠ 'Khách lẻ'</li>
            </ul>
            <p><strong>⚡ Quy trình xử lý:</strong></p>
            <ul style={{ marginLeft: 20 }}>
              <li><strong style={{ color: '#1890ff' }}>Chưa duyệt:</strong> Nhấn nút "Duyệt ĐL" để duyệt nhân viên vào nhóm đại lý trước</li>
              <li><strong style={{ color: '#52c41a' }}>Đã duyệt:</strong> Có thể nhấn nút "Tính TL" để tính thù lao cho đại lý</li>
              <li><strong style={{ color: '#f5222d' }}>Đã hủy:</strong> Không thể thực hiện thao tác nào</li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={invoices}
          loading={loading}
          rowKey="invoice_id"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Không có dữ liệu hóa đơn"
              />
            )
          }}
          size="middle"
        />
        
        {/* Additional Pagination Controls */}
        <div style={{ 
          marginTop: 16, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <Space>
            <Button 
              disabled={pagination.current <= 1}
              onClick={() => goToPage(1)}
              title="Về trang đầu tiên"
            >
              « Trang đầu
            </Button>
            <Button 
              disabled={pagination.current <= 1}
              onClick={() => goToPage(pagination.current - 1)}
              title="Về trang trước"
            >
              ‹ Trang trước
            </Button>
          </Space>
          
          <Space>
            <Text strong>
              Trang {pagination.current}
            </Text>
            <Text type="secondary">
              | {invoices.length} hóa đơn/trang
            </Text>
          </Space>
          
          <Space>
            <Button 
              disabled={invoices.length < pagination.pageSize}
              onClick={() => goToPage(pagination.current + 1)}
              type="primary"
              title="Đến trang tiếp theo"
            >
              Trang tiếp ›
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => loadInvoices()}
              title="Tải lại trang hiện tại"
            >
              Tải lại
            </Button>
          </Space>
        </div>
      </Card>

      {/* Commission Calculation Modal */}
      <Modal
        title={
          <Space>
            <CalculatorOutlined style={{ color: '#52c41a' }} />
            Kết quả tính thù lao đại lý
          </Space>
        }
        visible={commissionModal.visible}
        onCancel={handleCloseCommissionModal}
        footer={[
          <Button key="close" onClick={handleCloseCommissionModal}>
            Đóng
          </Button>
        ]}
        width={800}
        destroyOnClose
      >
        {commissionModal.loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Đang tính toán thù lao...</p>
          </div>
        ) : commissionModal.result ? (
          <div>
            {/* Invoice Info */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Mã hóa đơn">
                  <Text strong>{commissionModal.selectedInvoice?.code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Khách hàng">
                  {commissionModal.selectedInvoice?.customer_name}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {commissionModal.result.success ? (
              <div>
                {/* Success Result */}
                <Alert
                  message={
                    <Space>
                      <CheckCircleOutlined />
                      {commissionModal.result.message}
                    </Space>
                  }
                  type="success"
                  showIcon={false}
                  style={{ marginBottom: 16 }}
                />

                {commissionModal.result.data?.commissionResults && (
                  <div>
                    <Title level={5}>Chi tiết thù lao:</Title>
                    <Table
                      dataSource={commissionModal.result.data.commissionResults}
                      pagination={false}
                      size="small"
                      rowKey="memberid"
                      columns={[
                        {
                          title: 'Member ID',
                          dataIndex: 'memberid',
                          key: 'memberid'
                        },
                        {
                          title: 'Cấp',
                          dataIndex: 'cap',
                          key: 'cap',
                          render: (cap) => `N${cap}`
                        },
                        {
                          title: 'Thù lao',
                          dataIndex: 'thu_lao',
                          key: 'thu_lao',
                          render: (amount) => (
                            <Text strong style={{ color: '#f5222d' }}>
                              {formatCurrency(amount)}
                            </Text>
                          )
                        },
                        {
                          title: 'Doanh số',
                          dataIndex: 'doanh_so',
                          key: 'doanh_so',
                          render: (amount) => formatCurrency(amount)
                        },
                        {
                          title: 'Tỷ lệ (%)',
                          dataIndex: 'ty_le',
                          key: 'ty_le',
                          render: (rate) => `${rate}%`
                        }
                      ]}
                    />

                    <Divider />
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Tổng thù lao"
                          value={commissionModal.result.data.totalCommissionAmount}
                          formatter={(value) => formatCurrency(value)}
                          valueStyle={{ color: '#cf1322' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Số thành viên được hưởng"
                          value={commissionModal.result.data.affectedMembers}
                          valueStyle={{ color: '#1890ff' }}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Not Eligible */}
                <Alert
                  message={
                    <Space>
                      <ExclamationCircleOutlined />
                      {commissionModal.result.message}
                    </Space>
                  }
                  type="warning"
                  showIcon={false}
                  style={{ marginBottom: 16 }}
                />

                {commissionModal.result.data?.eligibilityCheck && (
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Lý do">
                      {commissionModal.result.data.eligibilityCheck.message}
                    </Descriptions.Item>
                    {commissionModal.result.data.reason && (
                      <Descriptions.Item label="Chi tiết">
                        {commissionModal.result.data.reason}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                )}
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default CommissionApproval;